const MAX_SNAPSHOT_POINTS = 240;

const DEFAULT_CHART_COLORS = [
  '#3A6A7A',
  '#3A5A2A',
  '#8B4513',
  '#4682B4',
  '#6C4AB6',
];

const safeArray = (value) => (Array.isArray(value) ? value : []);
const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const translate = (t, key, defaultValue, options = {}) => {
  if (typeof t !== 'function') {
    return defaultValue || key;
  }
  return t(key, { defaultValue, ...options });
};

const cloneSerializable = (value) => {
  if (value == null) {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const capRows = (rows) => safeArray(rows).slice(-MAX_SNAPSHOT_POINTS);

const getHourlyTickFormat = (timeRange) => {
  const days = Number(timeRange?.days);
  if (Number.isFinite(days) && days <= 1) {
    return 'HH:mm';
  }
  if (Number.isFinite(days) && days <= 7) {
    return 'EEE HH:mm';
  }
  return 'MMM d';
};

const buildSourceMeta = (source) => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }
  return {
    snapshot: true,
    ...source,
  };
};

const cloneArray = (value) => (Array.isArray(value) ? [...value] : []);

const resolveTimeRangeById = (ctx, timeRangeId) => {
  const candidates = safeArray(ctx?.TIME_RANGES);
  if (timeRangeId) {
    const matched = candidates.find(item => item?.id === timeRangeId);
    if (matched) {
      return matched;
    }
  }
  return ctx?.timeRange || candidates[0] || null;
};

const estimateHourlyCoverageDays = (hourlyData) => {
  const field = safeArray(hourlyData?.FIELD);
  if (!field.length) {
    return 0;
  }
  const approxDays = field.length / (24 * 4);
  return Math.max(0, Math.floor(approxDays));
};

const sliceHourlyRowsByDays = (rows, days) => {
  const safeRows = safeArray(rows);
  if (!Number.isFinite(days) || days <= 0) {
    return safeRows;
  }
  const points = Math.max(1, (Math.ceil(days) * 24 * 4) + 1);
  return safeRows.slice(-points);
};

const sliceDailyRowsByDays = (rows, days) => {
  const safeRows = safeArray(rows);
  if (!Number.isFinite(days) || days <= 0) {
    return safeRows;
  }
  return safeRows.slice(-Math.max(1, Math.ceil(days)));
};

const sliceAnalyticsDataByTimeRange = ({ hourlyData, dailyData, days }) => ({
  hourlyData: {
    FIELD: sliceHourlyRowsByDays(hourlyData?.FIELD, days),
    UNDER: sliceHourlyRowsByDays(hourlyData?.UNDER, days),
  },
  dailyData: {
    FIELD: sliceDailyRowsByDays(dailyData?.FIELD, days),
    UNDER: sliceDailyRowsByDays(dailyData?.UNDER, days),
  },
});

const buildLineVisualNodeData = ({
  t,
  title,
  titleKey,
  titleDefault,
  description,
  chartData,
  xKey,
  xAxis,
  series,
  yAxis,
  source,
  sourceInputs,
}) => {
  const resolvedTitle = title
    || translate(t, titleKey, titleDefault || 'Chart');
  const normalizedSeries = safeArray(series).map((item, index) => ({
    key: item.key,
    label: item.label,
    color: item.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
    strokeWidth: item.strokeWidth,
  })).filter(item => item.key);

  return {
    visualType: 'chart',
    title: resolvedTitle,
    captionLabel: translate(t, 'reports.review.template.placeholders.caption', 'Caption'),
    captionPlaceholder: translate(
      t,
      'reports.review.editor.insert.chartCaptionPlaceholder',
      'Add a short caption for this chart...'
    ),
    caption: '',
    description,
    chart: {
      variant: 'line',
      data: capRows(chartData),
      xKey: xKey || 'time',
      yKey: normalizedSeries[0]?.key || 'value',
      ...(normalizedSeries.length ? { series: normalizedSeries } : {}),
      ...(xAxis ? { xAxis } : {}),
      ...(yAxis ? { yAxis } : {}),
      showLegend: normalizedSeries.length > 1,
    },
    source: buildSourceMeta({
      ...source,
      ...(sourceInputs && typeof sourceInputs === 'object'
        ? { inputs: sourceInputs }
        : {}),
    }),
  };
};

const buildTemplateDescription = ({ t, sectionPath = [] }) => {
  const sourceLabel = translate(
    t,
    'reports.review.editor.insert.visualPicker.descriptions.templateSource',
    'Template visual'
  );
  const path = safeArray(sectionPath).filter(Boolean).join(' / ');
  return path ? `${path} • ${sourceLabel}` : sourceLabel;
};

const normalizeTemplateVisualNodeData = ({ block, visualType, sourceId, sectionId }) => {
  const next = cloneSerializable(block) || {};
  delete next.type;
  next.visualType = visualType;
  if (visualType === 'map' && next.chart && typeof next.chart === 'object') {
    next.chart = {
      ...next.chart,
      // Force map visuals inserted via picker to render from serialized snapshot data when present.
      preferFallbackCoverage: true,
    };
  }
  next.source = buildSourceMeta({
    kind: 'templateBlock',
    id: sourceId,
    sectionId: sectionId || undefined,
    inputs: {
      kind: 'templateBlock',
      templateVisualId: sourceId,
    },
  });
  return next;
};

export const buildBlankVisualNodeData = ({ visualType, t } = {}) => {
  if (visualType === 'map') {
    return {
      visualType: 'map',
      title: translate(t, 'reports.review.editor.insert.mapBlockTitle', 'Map block'),
      captionLabel: translate(t, 'reports.review.template.placeholders.caption', 'Caption'),
      captionPlaceholder: translate(
        t,
        'reports.review.editor.insert.mapCaptionPlaceholder',
        'Add a short caption for this map...'
      ),
      caption: '',
      chart: {
        variant: 'map',
        showLegend: true,
        legendTitle: translate(t, 'reports.workspace.blocks.coverage.legendTitle', 'Station types'),
      },
      source: buildSourceMeta({
        kind: 'systemPreset',
        id: 'blank:map',
        inputs: {
          kind: 'systemPreset',
        },
      }),
    };
  }

  return {
    visualType: 'chart',
    title: translate(t, 'reports.review.editor.insert.chartBlockTitle', 'Chart block'),
    captionLabel: translate(t, 'reports.review.template.placeholders.caption', 'Caption'),
    captionPlaceholder: translate(
      t,
      'reports.review.editor.insert.chartCaptionPlaceholder',
      'Add a short caption for this chart...'
    ),
    caption: '',
    chart: {
      xKey: 'label',
      yKey: 'value',
      data: [
        { label: 'Apr', value: 3.2 },
        { label: 'May', value: 3.0 },
        { label: 'Jun', value: 2.7 },
        { label: 'Jul', value: 2.3 },
        { label: 'Aug', value: 2.1 },
      ],
    },
    source: buildSourceMeta({
      kind: 'systemPreset',
      id: 'blank:chart',
      inputs: {
        kind: 'systemPreset',
      },
    }),
  };
};

export function collectTemplateVisualInsertItems(templateDocument, t) {
  const sections = safeArray(templateDocument?.sections);
  const items = [];

  const visitSections = (nodes, sectionPath = []) => {
    safeArray(nodes).forEach((section, sectionIndex) => {
      const title = typeof section?.title === 'string' ? section.title.trim() : '';
      const nextPath = title ? [...sectionPath, title] : sectionPath;
      safeArray(section?.blocks).forEach((block, blockIndex) => {
        const visualType = block?.type === 'map' ? 'map' : (block?.type === 'chart' ? 'chart' : null);
        if (!visualType) {
          return;
        }
        const fallbackId = section?.id || `section-${sectionIndex + 1}`;
        const blockId = block?.id || `${visualType}-${blockIndex + 1}`;
        const itemId = `template:${fallbackId}:${blockId}`;
        const label = String(block?.title || '').trim() || translate(
          t,
          visualType === 'map'
            ? 'reports.review.editor.insert.mapBlockTitle'
            : 'reports.review.editor.insert.chartBlockTitle',
          visualType === 'map' ? 'Map block' : 'Chart block'
        );

        items.push({
          id: itemId,
          label,
          description: buildTemplateDescription({ t, sectionPath: nextPath }),
          visualType,
          sourceKind: 'templateBlock',
          nodeData: normalizeTemplateVisualNodeData({
            block,
            visualType,
            sourceId: itemId,
            sectionId: section?.id || undefined,
          }),
        });
      });
      visitSections(section?.children, nextPath);
    });
  };

  visitSections(sections, []);
  return items;
}

const fieldLabel = (t) => translate(t, 'stations.types.field', 'Field');
const underLabel = (t) => translate(t, 'stations.types.underpanel', 'Under panel');

const buildAnalyticsDescription = (t, tabLabelKey, tabLabelDefault) => {
  const prefix = translate(
    t,
    'reports.review.editor.insert.visualPicker.descriptions.analyticsSource',
    'Analytics snapshot'
  );
  const tabLabel = translate(t, tabLabelKey, tabLabelDefault);
  return `${prefix} • ${tabLabel}`;
};

const getHourlyPair = (hourlyData) => ({
  field: safeArray(hourlyData?.FIELD),
  under: safeArray(hourlyData?.UNDER),
});

const getDailyPair = (dailyData) => ({
  field: safeArray(dailyData?.FIELD),
  under: safeArray(dailyData?.UNDER),
});

const buildOverviewRadiationData = ({ hourlyData }) => {
  const { field, under } = getHourlyPair(hourlyData);
  if (!field.length || !under.length) return [];
  const lastField = field.slice(-96);
  const lastUnder = under.slice(-96);
  return lastField.map((row, index) => {
    const underRow = lastUnder[index] || {};
    return {
      time: row.ts_iso,
      campo_ghi: toNumber(row.ghi_wm2),
      retro_ghi: toNumber(underRow.ghi_wm2),
    };
  });
};

const sampleHourPairs = ({ hourlyData, limitDivisor = 200, mapper }) => {
  const { field, under } = getHourlyPair(hourlyData);
  if (!field.length) return [];
  const step = Math.max(1, Math.floor(field.length / limitDivisor));
  return field.filter((_, index) => index % step === 0).map((row, sampleIndex) => {
    const underRow = under[sampleIndex * step] || {};
    return mapper(row, underRow, sampleIndex * step);
  }).filter(Boolean);
};

const buildSoilMoisture10cmData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 200,
  mapper: (fieldRow, underRow) => ({
    time: fieldRow.ts_iso,
    campo_10cm: toNumber(fieldRow.soil_vwc_pct_10cm),
    retro_10cm: toNumber(underRow.soil_vwc_pct_10cm),
  }),
});

const buildSoilPhData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 150,
  mapper: (fieldRow, underRow) => ({
    time: fieldRow.ts_iso,
    campo_ph: toNumber(fieldRow.soil_ph),
    retro_ph: toNumber(underRow.soil_ph),
  }),
});

const buildSoilEcData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 150,
  mapper: (fieldRow, underRow) => ({
    time: fieldRow.ts_iso,
    campo_ec: toNumber(fieldRow.soil_ec_dsm),
    retro_ec: toNumber(underRow.soil_ec_dsm),
  }),
});

const buildClimateVpdData = ({ hourlyData }) => {
  const { field, under } = getHourlyPair(hourlyData);
  if (!field.length) return [];
  const lastField = field.slice(-168);
  const lastUnder = under.slice(-168);
  return lastField.map((row, index) => {
    const underRow = lastUnder[index] || {};
    const hour = row?.ts_iso ? new Date(row.ts_iso).getHours() : null;
    if (!Number.isFinite(hour) || hour < 6 || hour > 20) {
      return null;
    }
    return {
      time: row.ts_iso,
      campo_vpd: toNumber(row.vpd_kpa),
      retro_vpd: toNumber(underRow.vpd_kpa),
    };
  }).filter(Boolean);
};

const buildClimateHumidityData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 200,
  mapper: (fieldRow, underRow) => ({
    time: fieldRow.ts_iso,
    campo_rh: toNumber(fieldRow.rh_pct),
    retro_rh: toNumber(underRow.rh_pct),
  }),
});

const buildClimatePressureData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 150,
  mapper: (fieldRow) => ({
    time: fieldRow.ts_iso,
    pressure: toNumber(fieldRow.pressure_hpa, 1013),
  }),
});

const buildWindSpeedData = ({ hourlyData }) => sampleHourPairs({
  hourlyData,
  limitDivisor: 200,
  mapper: (fieldRow, underRow) => ({
    time: fieldRow.ts_iso,
    campo_speed: toNumber(fieldRow.wind_ms),
    retro_speed: toNumber(underRow.wind_ms),
  }),
});

const buildRadiationShadeFactorData = ({ dailyData }) => {
  const { field, under } = getDailyPair(dailyData);
  if (!field.length) return [];
  const lastField = field.slice(-30);
  const lastUnder = under.slice(-30);
  return lastField.map((row, index) => {
    const underRow = lastUnder[index] || {};
    const campoPsh = toNumber(row.psh);
    const retroPsh = toNumber(underRow.psh);
    const median = campoPsh > 0 ? ((campoPsh - retroPsh) / campoPsh) * 100 : 0;
    return {
      date: row.date,
      median: Number(median.toFixed(2)),
    };
  });
};

const buildSoilWaterDeltaVwcData = ({ hourlyData }) => {
  const { field, under } = getHourlyPair(hourlyData);
  if (!field.length) return [];
  const lastField = field.slice(-168);
  const lastUnder = under.slice(-168);
  return lastField.map((row, index) => {
    const underRow = lastUnder[index] || {};
    return {
      time: row.ts_iso,
      delta_10cm: Number((toNumber(underRow.soil_vwc_pct_10cm) - toNumber(row.soil_vwc_pct_10cm)).toFixed(2)),
      delta_30cm: Number((toNumber(underRow.soil_vwc_pct_30cm) - toNumber(row.soil_vwc_pct_30cm)).toFixed(2)),
    };
  });
};

const buildAnalyticsPresets = (ctx) => {
  const { t } = ctx;
  const field = fieldLabel(t);
  const under = underLabel(t);
  const hourlyTickFormat = getHourlyTickFormat(ctx.timeRange);

  return [
    {
      id: 'analytics:overview:radiation-overview',
      tabId: 'overview',
      visualType: 'chart',
      label: translate(t, 'analytics.overview.radiationOverviewTitle', 'Radiation overview'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.overview', 'Overview'),
      buildRows: buildOverviewRadiationData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.overview.radiationOverviewTitle', 'Radiation overview'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: {
          label: translate(t, 'analytics.radiation.axes.ghiWm2', 'GHI (W/m²)'),
        },
        series: [
          { key: 'campo_ghi', label: field, color: '#3A6A7A' },
          { key: 'retro_ghi', label: under, color: '#3A5A2A' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:overview:radiation-overview',
          tabId: 'overview',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:overview:radiation-overview',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:soil-sensors:moisture-10cm',
      tabId: 'soil-sensors',
      visualType: 'chart',
      label: `${translate(t, 'analytics.soilSensors.moisture.title', 'Soil moisture')} (10cm)`,
      description: buildAnalyticsDescription(t, 'analytics.tabs.soil', 'Soil'),
      buildRows: buildSoilMoisture10cmData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: `${translate(t, 'analytics.soilSensors.moisture.title', 'Soil moisture')} (10cm)`,
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: translate(t, 'analytics.soilSensors.axes.vwcPercent', 'VWC (%)') },
        series: [
          { key: 'campo_10cm', label: field, color: '#8B4513' },
          { key: 'retro_10cm', label: under, color: '#4682B4' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:soil-sensors:moisture-10cm',
          tabId: 'soil-sensors',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:soil-sensors:moisture-10cm',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:soil-sensors:ph',
      tabId: 'soil-sensors',
      visualType: 'chart',
      label: translate(t, 'analytics.soilSensors.ph.title', 'Soil pH'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.soil', 'Soil'),
      buildRows: buildSoilPhData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.soilSensors.ph.title', 'Soil pH'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: 'pH' },
        series: [
          { key: 'campo_ph', label: field, color: '#5A7A3A' },
          { key: 'retro_ph', label: under, color: '#3A6A7A' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:soil-sensors:ph',
          tabId: 'soil-sensors',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:soil-sensors:ph',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:soil-sensors:ec',
      tabId: 'soil-sensors',
      visualType: 'chart',
      label: translate(t, 'analytics.soilSensors.ec.title', 'Soil EC'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.soil', 'Soil'),
      buildRows: buildSoilEcData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.soilSensors.ec.title', 'Soil EC'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: 'EC (dS/m)' },
        series: [
          { key: 'campo_ec', label: field, color: '#7A5A3A' },
          { key: 'retro_ec', label: under, color: '#4A6FB3' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:soil-sensors:ec',
          tabId: 'soil-sensors',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:soil-sensors:ec',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:climate:vpd',
      tabId: 'climate',
      visualType: 'chart',
      label: translate(t, 'analytics.climate.vpd.title', 'VPD'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.climate', 'Climate'),
      buildRows: buildClimateVpdData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.climate.vpd.title', 'VPD'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: translate(t, 'analytics.climate.axes.vpdKpa', 'VPD (kPa)') },
        series: [
          { key: 'campo_vpd', label: translate(t, 'analytics.climate.series.fieldVpd', field), color: '#C55A3A' },
          { key: 'retro_vpd', label: translate(t, 'analytics.climate.series.underVpd', under), color: '#3A6A7A' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:climate:vpd',
          tabId: 'climate',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:climate:vpd',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:climate:humidity',
      tabId: 'climate',
      visualType: 'chart',
      label: translate(t, 'analytics.climate.humidity.title', 'Humidity'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.climate', 'Climate'),
      buildRows: buildClimateHumidityData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.climate.humidity.title', 'Humidity'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: translate(t, 'analytics.climate.axes.rhPercent', 'RH (%)') },
        series: [
          { key: 'campo_rh', label: field, color: '#4A7A8A' },
          { key: 'retro_rh', label: under, color: '#5C8A5C' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:climate:humidity',
          tabId: 'climate',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:climate:humidity',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:climate:pressure',
      tabId: 'climate',
      visualType: 'chart',
      label: translate(t, 'analytics.climate.pressure.title', 'Pressure'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.climate', 'Climate'),
      buildRows: buildClimatePressureData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.climate.pressure.title', 'Pressure'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: 'hPa' },
        series: [
          { key: 'pressure', label: translate(t, 'analytics.climate.pressure.title', 'Pressure'), color: '#4F6070' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:climate:pressure',
          tabId: 'climate',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:climate:pressure',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:wind:speed',
      tabId: 'wind',
      visualType: 'chart',
      label: translate(t, 'analytics.wind.speedTimeSeriesTitle', 'Wind speed'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.wind', 'Wind'),
      buildRows: buildWindSpeedData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.wind.speedTimeSeriesTitle', 'Wind speed'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: `${translate(t, 'analytics.wind.speed', 'Wind speed')} (m/s)` },
        series: [
          { key: 'campo_speed', label: translate(t, 'analytics.wind.series.fieldSpeed', field), color: '#3A6A7A' },
          { key: 'retro_speed', label: translate(t, 'analytics.wind.series.underSpeed', under), color: '#3A5A2A' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:wind:speed',
          tabId: 'wind',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:wind:speed',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:radiation:shade-factor',
      tabId: 'radiation',
      visualType: 'chart',
      label: translate(t, 'analytics.radiation.shadeFactorTimelineTitle', 'Shade factor timeline'),
      description: buildAnalyticsDescription(t, 'analytics.tabs.radiation', 'Radiation'),
      buildRows: buildRadiationShadeFactorData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'analytics.radiation.shadeFactorTimelineTitle', 'Shade factor timeline'),
        chartData: rows,
        xKey: 'date',
        xAxis: { valueType: 'isoDate', tickFormat: 'MMM d' },
        yAxis: { label: translate(t, 'analytics.radiation.axes.shadeFactorPercent', 'Shade factor (%)') },
        series: [
          { key: 'median', label: translate(t, 'analytics.radiation.medianShade', 'Median shade'), color: '#6C7A3A' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:radiation:shade-factor',
          tabId: 'radiation',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:radiation:shade-factor',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
    {
      id: 'analytics:soil-water:delta-vwc',
      tabId: 'soil-water',
      visualType: 'chart',
      label: translate(t, 'reports.review.editor.insert.visualPicker.analyticsLabels.soilWaterDeltaVwc', 'ΔVWC by Depth'),
      description: buildAnalyticsDescription(t, 'reports.review.editor.insert.visualPicker.analyticsTabs.soilWater', 'Soil water'),
      buildRows: buildSoilWaterDeltaVwcData,
      buildNodeData: (rows) => buildLineVisualNodeData({
        t,
        title: translate(t, 'reports.review.editor.insert.visualPicker.analyticsLabels.soilWaterDeltaVwc', 'ΔVWC by Depth'),
        chartData: rows,
        xKey: 'time',
        xAxis: { valueType: 'isoDateTime', tickFormat: hourlyTickFormat },
        yAxis: { label: 'ΔVWC (pp)' },
        series: [
          { key: 'delta_10cm', label: 'Δ10cm', color: '#8B4513' },
          { key: 'delta_30cm', label: 'Δ30cm', color: '#4682B4' },
        ],
        source: {
          kind: 'analyticsPreset',
          id: 'analytics:soil-water:delta-vwc',
          tabId: 'soil-water',
          farmId: ctx.selectedFarm?.id,
          timeRangeId: ctx.timeRange?.id,
        },
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: 'analytics:soil-water:delta-vwc',
          timeRangeId: ctx.timeRange?.id,
          sectionFilterMode: 'pageSelected',
          sectionIds: cloneArray(ctx.selectedSections),
          farmId: ctx.selectedFarm?.id,
        },
      }),
    },
  ];
};

export function buildAnalyticsVisualPresetItems(ctx = {}) {
  const presets = buildAnalyticsPresets(ctx);
  return presets.map((preset) => {
    const rows = capRows(preset.buildRows(ctx));
    if (!rows.length) {
      return {
        id: preset.id,
        tabId: preset.tabId,
        visualType: preset.visualType,
        sourceKind: 'analyticsPreset',
        label: preset.label,
        description: preset.description,
        disabled: true,
        disabledReason: translate(
          ctx.t,
          'reports.review.editor.insert.visualPicker.disabled.noData',
          'No data in current analytics selection'
        ),
      };
    }

    const nodeData = preset.buildNodeData(rows);
    return {
      id: preset.id,
      tabId: preset.tabId,
      visualType: preset.visualType,
      sourceKind: 'analyticsPreset',
      label: preset.label,
      description: preset.description,
      disabled: false,
      nodeData,
    };
  });
}

export function buildAnalyticsVisualSnapshotByPresetId(ctx = {}, presetId, options = {}) {
  const effectiveTimeRange = resolveTimeRangeById(ctx, options.timeRangeId);
  const requestedDays = Number(effectiveTimeRange?.days);
  const currentCoverageDays = estimateHourlyCoverageDays(ctx.hourlyData);
  if (
    Number.isFinite(requestedDays)
    && requestedDays > 0
    && Number.isFinite(currentCoverageDays)
    && currentCoverageDays > 0
    && requestedDays > currentCoverageDays
  ) {
    return {
      ok: false,
      reason: 'insufficient_time_range_data',
      requestedTimeRangeId: effectiveTimeRange?.id,
      availableDays: currentCoverageDays,
    };
  }

  const sliced = sliceAnalyticsDataByTimeRange({
    hourlyData: ctx.hourlyData,
    dailyData: ctx.dailyData,
    days: requestedDays,
  });

  const baseCtx = {
    ...ctx,
    ...sliced,
    timeRange: effectiveTimeRange || ctx.timeRange,
    selectedSections: options.sectionFilterMode === 'custom'
      ? cloneArray(options.sectionIds)
      : cloneArray(ctx.selectedSections),
  };

  const preset = buildAnalyticsPresets(baseCtx).find(item => item.id === presetId);
  if (!preset) {
    return { ok: false, reason: 'preset_not_found' };
  }

  const rows = capRows(preset.buildRows(baseCtx));
  if (!rows.length) {
    return { ok: false, reason: 'no_data' };
  }

  const nodeData = preset.buildNodeData(rows);
  const nextSource = nodeData?.source && typeof nodeData.source === 'object'
    ? {
      ...nodeData.source,
      inputs: {
        ...(nodeData.source.inputs && typeof nodeData.source.inputs === 'object' ? nodeData.source.inputs : {}),
        kind: 'analyticsPreset',
        presetId,
        timeRangeId: effectiveTimeRange?.id || options.timeRangeId || ctx.timeRange?.id,
        sectionFilterMode: options.sectionFilterMode === 'custom' ? 'custom' : 'pageSelected',
        sectionIds: options.sectionFilterMode === 'custom'
          ? cloneArray(options.sectionIds)
          : cloneArray(ctx.selectedSections),
        farmId: ctx.selectedFarm?.id,
      },
      updatedAt: new Date().toISOString(),
    }
    : undefined;

  return {
    ok: true,
    nodeData: {
      ...nodeData,
      ...(nextSource ? { source: nextSource } : {}),
    },
  };
}

export function getTemplateVisualNodeDataByItemId(templateDocument, itemId, t) {
  const items = collectTemplateVisualInsertItems(templateDocument, t);
  const matched = items.find(item => item.id === itemId);
  return matched?.nodeData ? cloneSerializable(matched.nodeData) : null;
}

export function getCoverageMapPresetNodeData({ selectedFarm, t } = {}) {
  const item = buildCurrentCoverageMapVisualItem({ selectedFarm, t });
  return item?.disabled ? null : cloneSerializable(item.nodeData);
}

const buildCoverageSnapshotFromFarm = (farm) => {
  if (!farm || !Array.isArray(farm.sections) || farm.sections.length === 0) {
    return null;
  }

  const zones = farm.sections.map((section, index) => ({
    id: section?.id || `zone-${index + 1}`,
    name: section?.name || section?.locationDescription || `Zone ${index + 1}`,
    areaHa: section?.areaHa,
    polygon: Array.isArray(section?.polygon) ? section.polygon : undefined,
    bounds: section?.bounds,
    position: section?.position,
  }));

  const stations = safeArray(farm.stations).map((station) => ({
    id: station?.id,
    name: station?.name || station?.stationCode || station?.locationDescription,
    type: station?.type || station?.stationType || station?.sensorType,
    sensorType: station?.sensorType || station?.type,
    stationType: station?.stationType || station?.type || station?.sensorType,
    locationLat: station?.locationLat,
    locationLon: station?.locationLon,
    lat: station?.lat,
    lon: station?.lon,
    latitude: station?.latitude,
    longitude: station?.longitude,
    coordinates: (
      station?.locationLat != null && station?.locationLon != null
        ? [station.locationLat, station.locationLon]
        : (station?.lat != null && station?.lon != null
          ? [station.lat, station.lon]
          : undefined)
    ),
    zoneId: station?.sectionId || station?.zoneId || null,
    sectionId: station?.sectionId || station?.zoneId || null,
    locationDescription: station?.locationDescription || station?.name || station?.stationCode,
  }));

  return {
    id: farm.id,
    name: farm.name || farm.title || 'Coverage',
    zones,
    stations,
  };
};

export function buildCurrentCoverageMapVisualItem({ selectedFarm, t } = {}) {
  const fallbackCoverage = buildCoverageSnapshotFromFarm(selectedFarm);
  const label = translate(
    t,
    'reports.review.editor.insert.visualPicker.coverage.currentFarmMapLabel',
    'Current farm coverage map'
  );
  const description = translate(
    t,
    'reports.review.editor.insert.visualPicker.coverage.currentFarmMapDescription',
    'Snapshot of the current analytics farm coverage and station distribution.'
  );

  if (!fallbackCoverage) {
    return {
      id: 'coverage:current-farm-map',
      label,
      description,
      visualType: 'map',
      sourceKind: 'systemPreset',
      disabled: true,
      disabledReason: translate(
        t,
        'reports.review.editor.insert.visualPicker.disabled.noData',
        'No data in current analytics selection'
      ),
    };
  }

  return {
    id: 'coverage:current-farm-map',
    label,
    description,
    visualType: 'map',
    sourceKind: 'systemPreset',
    disabled: false,
    nodeData: {
      visualType: 'map',
      title: label,
      captionLabel: translate(t, 'reports.review.template.placeholders.caption', 'Caption'),
      captionPlaceholder: translate(
        t,
        'reports.review.editor.insert.mapCaptionPlaceholder',
        'Add a short caption for this map...'
      ),
      caption: '',
      chart: {
        variant: 'map',
        showLegend: true,
        legendTitleKey: 'reports.workspace.blocks.coverage.legendTitle',
        preferFallbackCoverage: true,
      },
      fallbackCoverage,
      source: buildSourceMeta({
        kind: 'systemPreset',
        id: 'coverage:current-farm-map',
        farmId: selectedFarm?.id,
        inputs: {
          kind: 'systemPreset',
          farmId: selectedFarm?.id,
        },
      }),
    },
  };
}

export { MAX_SNAPSHOT_POINTS };
